import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Alert,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Event as EventIcon,
} from "@mui/icons-material";

const AssessorSearchCalendarView = ({ assessorId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [occupancyData, setOccupancyData] = useState([]);
  const [loading, setLoading] = useState(false);

  // API Call - Merge data from both APIs
  const GetAssessorOccupancyDetails = async (assessorId, month, year) => {
    if (!assessorId) return;
    try {
      setLoading(true);
      
      // Format month to 2 digits (01-12)
      const formattedMonth = String(month).padStart(2, '0');
      
      // Call both APIs in parallel
      const [internalResponse, externalResponse] = await Promise.allSettled([
        axios.get(
          `${process.env.REACT_APP_API_URL}/assessor/GetAssessorOccupancyDetails?assessorId=${assessorId}&month=${month}&year=${year}`
        ),
        axios.get(
          `${process.env.REACT_APP_DOT_NET_CORE_API_URL}/Assessoroccupancy?assesorid=${assessorId}&month=${formattedMonth}&year=${year}`
        )
      ]);

      let mergedData = [];

      // Process internal API data
      if (internalResponse.value?.data?.success) {
        const internalData = internalResponse.value.data.data || [];
        mergedData = [...internalData];
      }

      // Process external API data and normalize field names
      if (externalResponse.value?.data?.isstatus) {
        const externalData = externalResponse.value.data.assessor || [];
        const normalizedExternalData = externalData.map(item => ({
          appid: item.appid,
          cabName: item.cabName,
          ApplicationNo: item.applicationNo,
          DateFrom: item.datefrom,
          DateTo: item.dateto
        }));
        mergedData = [...mergedData, ...normalizedExternalData];
      }

      // Remove duplicates based on appid, DateFrom, and DateTo
      const uniqueData = mergedData.filter((item, index, self) =>
        index === self.findIndex((t) => (
          t.appid === item.appid && 
          t.DateFrom === item.DateFrom && 
          t.DateTo === item.DateTo
        ))
      );

      // Sort by DateFrom (ascending order - earliest first)
      const sortedData = uniqueData.sort((a, b) => {
        const dateA = parseDate(a.DateFrom);
        const dateB = parseDate(b.DateFrom);
        
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        
        // First sort by DateFrom
        const dateFromDiff = dateA - dateB;
        if (dateFromDiff !== 0) return dateFromDiff;
        
        // If DateFrom is same, sort by DateTo
        const dateToA = parseDate(a.DateTo);
        const dateToB = parseDate(b.DateTo);
        
        if (!dateToA && !dateToB) return 0;
        if (!dateToA) return 1;
        if (!dateToB) return -1;
        
        return dateToA - dateToB;
      });
      setOccupancyData(sortedData);
    } catch (err) {
      console.error("Error fetching occupancy:", err);
      setOccupancyData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const month = currentDate.getMonth() + 1;
    const year = currentDate.getFullYear();
    GetAssessorOccupancyDetails(assessorId, month, year);
  }, [assessorId, currentDate]);

  // Parse dd-MM-yyyy to Date
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("-");
    return new Date(year, month - 1, day);
  };

  const isDateOccupied = (day) => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const checkDate = new Date(year, month, day);
    checkDate.setHours(0, 0, 0, 0);

    return occupancyData.some((item) => {
      const fromDate = parseDate(item.DateFrom);
      const toDate = parseDate(item.DateTo);
      if (!fromDate || !toDate) return false;
      fromDate.setHours(0, 0, 0, 0);
      toDate.setHours(23, 59, 59, 999);
      return checkDate >= fromDate && checkDate <= toDate;
    });
  };

  const prevMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
    );
  };

  const nextMonth = () => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1)
    );
  };

  // Render Calendar Grid
  const renderCalendar = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();

    const weekdays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const cells = [];

    // Empty cells before first day
    for (let i = 0; i < firstDayOfMonth; i++) {
      cells.push(
        <TableCell key={`empty-${i}`} sx={{ border: "1px solid #e0e0e0" }} />
      );
    }

    // Day cells
    for (let day = 1; day <= daysInMonth; day++) {
      const occupied = isDateOccupied(day);
      cells.push(
        <TableCell
          key={day}
          sx={{
            border: "1px solid #e0e0e0",
            textAlign: "center",
            padding: "10px 4px",
            backgroundColor: occupied ? "#ff7675" : "inherit",
            color: occupied ? "#fff" : "inherit",
            fontWeight: occupied ? "bold" : "normal",
            "&:hover": {
              backgroundColor: occupied ? "#d63031" : "#f5f5f5",
              cursor: "default",
            },
          }}
        >
          {day}
        </TableCell>
      );
    }

    // Split into rows of 7
    const rows = [];
    for (let i = 0; i < cells.length; i += 7) {
      rows.push(<TableRow key={i}>{cells.slice(i, i + 7)}</TableRow>);
    }

    return (
      <Table size="small" sx={{ borderCollapse: "collapse" }}>
        <TableHead>
          <TableRow>
            {weekdays.map((day) => (
              <TableCell
                key={day}
                sx={{
                  border: "1px solid #e0e0e0",
                  textAlign: "center",
                  fontWeight: "bold",
                  backgroundColor: "#f5f7fa",
                  padding: "8px 4px",
                }}
              >
                {day}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>{rows}</TableBody>
      </Table>
    );
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        borderRadius: 3,
        backgroundColor: "#fff",
        maxWidth: "100%",
        overflowX: "auto",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>
        Assessor Occupancy
      </Typography>

      {/* Main Grid: Left = Calendar, Right = Table */}
      <Grid container spacing={2}>
        {/* Left Column: Calendar */}
        <Grid item xs={12} md={6}>
          {/* Month Navigation */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1,
            }}
          >
            <IconButton onClick={prevMonth} aria-label="previous month">
              <ChevronLeftIcon />
            </IconButton>
            <Typography variant="h5" fontWeight="bold" color="primary">
              {currentDate.toLocaleString("default", { month: "long" })}{" "}
              {currentDate.getFullYear()}
            </Typography>
            <IconButton onClick={nextMonth} aria-label="next month">
              <ChevronRightIcon />
            </IconButton>
          </Box>

          {/* Loading */}
          {loading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {/* Calendar Grid */}
          {!loading && <Box sx={{ mb: 2 }}>{renderCalendar()}</Box>}

          {/* Legend */}
          <Box sx={{ display: "flex", gap: 2, mb: 1 }}>
            <Chip
              label="Occupied / Allotted"
              sx={{
                backgroundColor: "#ff7675",
                color: "#fff",
                fontWeight: "bold",
              }}
            />
            <Chip
              label="Available"
              sx={{
                backgroundColor: "#e0e0e0",
                color: "#333",
                fontWeight: "bold",
              }}
            />
          </Box>
        </Grid>

        {/* Right Column: Table */}
        <Grid item xs={12} md={6}>
          <Card
            sx={{
              boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
              borderRadius: 3,
              overflow: "hidden",
              maxHeight: 400,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                p: 1,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#fff",
                  fontWeight: 700,
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <EventIcon />
                Assessor's Current Allotments
              </Typography>
            </Box>

            <CardContent sx={{ p: 0, overflowY: "auto", maxHeight: "calc(500px - 56px)" }}>
              {occupancyData.length > 0 ? (
                <TableContainer component={Paper} elevation={0}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                        <TableCell sx={{ fontWeight: 700, width: 60 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Laboratory Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>
                          Application No
                        </TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date From</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Date To</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {occupancyData.map((item, index) => (
                        <TableRow
                          key={index}
                          hover
                          sx={{
                            "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
                          }}
                        >
                          <TableCell>
                            <Typography fontWeight={600}>{index + 1}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={500}>
                              {item.cabName || "-"}
                            </Typography>
                          </TableCell>
                          <TableCell>{item.ApplicationNo || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              label={item.DateFrom || "-"}
                              color="success"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={item.DateTo || "-"}
                              color="error"
                              variant="outlined"
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 4, textAlign: "center" }}>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No Allotments Found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    No assessor allotments are available for this month.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default AssessorSearchCalendarView;